import { resolveTypeKey, semanticToVisibleType } from "./resolve";
import { semanticTypeToFieldType } from "./semanticMapping";
import {
  coerceDateOnly,
  coerceDateTimeLocal,
  coerceString,
  coerceTimeOnly,
  parseBoolean,
  parseCurrency,
  parseDateOnly,
  parseDateTime,
  parseNumber,
  parsePeriod,
  parseStringArray,
  parseTime,
  parseUrl,
  sanitizeCurrencyForApi,
} from "./parsers";

/**
 * Coerce a spreadsheet cell into a value suitable for the target field type.
 * Falls back to the original string when parsing fails (scalars) so the user can fix in review.
 */
export const coerceImportFieldValue = (rawValue, fieldType) => {
  if (rawValue == null || rawValue === "") return null;

  const type = resolveTypeKey(fieldType);
  const asString = String(rawValue).trim();
  if (!asString && !Array.isArray(rawValue)) return null;

  switch (type) {
    case "INTEGER":
      return parseNumber(asString, (value) => parseInt(value, 10)) ?? asString;
    case "DOUBLE":
      return parseNumber(asString, parseFloat) ?? asString;
    case "BOOLEAN":
      return parseBoolean(asString) ?? asString;
    case "DATE":
      return parseDateOnly(asString) ?? asString;
    case "DATETIME":
      return parseDateTime(asString) ?? asString;
    case "TIME":
      return parseTime(asString) ?? asString;
    case "CURRENCY":
      return parseCurrency(asString);
    case "URL":
      return parseUrl(asString);
    case "STRING_ARRAY":
      return parseStringArray(rawValue);
    case "PERIOD":
      return parsePeriod(rawValue) ?? asString;
    case "ENUM":
    case "COMBOLIST":
      return asString;
    default:
      return asString;
  }
};

export const resolveColumnFieldType = (column = {}) => {
  if (column.fieldType) {
    return resolveTypeKey(column.fieldType);
  }
  if (column.fieldTypeOverridden) {
    return resolveTypeKey(column.fieldType || "STRING");
  }
  return semanticTypeToFieldType(column.semanticType);
};

export const getDefaultColumnTypeDisplay = (column = {}) => {
  const semanticType = String(column.semanticType || "").trim();
  if (semanticType) {
    if (semanticType === "CUSTOM" && column.customSemanticType) {
      return column.customSemanticType;
    }
    return semanticType;
  }
  return semanticToVisibleType("STRING");
};

export const getColumnTypeDisplay = (column = {}) => {
  if (column.typeFallbackApplied) {
    return semanticToVisibleType(column.fieldType || "STRING");
  }
  if (column.fieldTypeOverridden) {
    return semanticToVisibleType(column.fieldType || "STRING");
  }
  return getDefaultColumnTypeDisplay(column);
};

/**
 * Build API-ready custom field payload from an imported spreadsheet cell.
 * @param {string} name
 * @param {string} fieldType
 * @param {*} coercedValue
 * @param {{ options?: string[] }} [context]
 */
export const buildImportFieldPayload = (name, fieldType, coercedValue, context = {}) => {
  const type = resolveTypeKey(fieldType);
  const trimmedName = String(name || "").trim();
  if (!trimmedName) return null;

  if (coercedValue == null || coercedValue === "") return null;

  switch (type) {
    case "PERIOD": {
      if (typeof coercedValue === "object" && coercedValue !== null) {
        const startDate = coerceDateOnly(coercedValue.startDate);
        const endDate = coerceDateOnly(coercedValue.endDate);
        if (!startDate && !endDate) return null;
        return { name: trimmedName, type, startDate, endDate };
      }
      return null;
    }
    case "ENUM":
    case "COMBOLIST": {
      const value = coerceString(coercedValue);
      if (!value) return null;
      const providedOptions = Array.isArray(context.options)
        ? context.options.map((item) => coerceString(item).trim()).filter(Boolean)
        : [];
      const options =
        providedOptions.length > 0
          ? providedOptions.includes(value)
            ? providedOptions
            : [...providedOptions, value]
          : [value];
      return {
        name: trimmedName,
        type,
        value,
        options,
      };
    }
    case "STRING_ARRAY":
      return {
        name: trimmedName,
        type,
        value: Array.isArray(coercedValue) ? coercedValue : parseStringArray(coercedValue),
      };
    case "CURRENCY": {
      let sanitized = null;
      if (typeof coercedValue === "object" && coercedValue !== null) {
        sanitized = sanitizeCurrencyForApi(coercedValue);
      } else {
        sanitized = sanitizeCurrencyForApi(parseCurrency(coercedValue));
      }
      return sanitized ? { name: trimmedName, type, value: sanitized } : null;
    }
    default:
      return { name: trimmedName, type, value: coercedValue };
  }
};
